import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  Popover,
  StepLabel,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import React, { useMemo, useState } from "react";
import { COLORS } from "../../assets/theme";

interface CustomMonthYearPickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  label?: string;
  fullWidth?: boolean;
}

const toUTCDateTime = (date: Date | null): Date | null =>
  date
    ? new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
      )
    : null;

const toLocalDateTime = (utcDate: Date | null): Date | null =>
  utcDate
    ? new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000)
    : null;

const CustomMonthYearPicker: React.FC<CustomMonthYearPickerProps> = ({
  value,
  onChange,
  label = "Invoice Month",
  fullWidth,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const localValue = useMemo(() => toLocalDateTime(value ?? null), [value]);

  const open = Boolean(anchorEl);
  const controlSx = fullWidth
    ? { width: "100%" }
    : { width: { xs: "100%", sm: "25%" }, maxWidth: "100%", minWidth: "220px" };

  const display = useMemo(
    () => (localValue ? format(localValue, "MMM yyyy") : null),
    [localValue],
  );

  const handleDateSelect = (selectedDate: Date | null) => {
    if (!selectedDate) return;
    const firstOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1,
    );
    onChange?.(toUTCDateTime(firstOfMonth));
    setAnchorEl(null);
  };

  const handleClear = () => {
    onChange?.(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl variant="outlined" sx={controlSx}>
        <StepLabel>{label}</StepLabel>
        <Box
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            columnGap: 1,
            height: "42px",
            width: "100%",
            padding: "6px 12px",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "12px",
            cursor: "pointer",
            fontSize: "14px",
            boxSizing: "border-box",
            "&:hover": {
              borderColor: "#868585",
            },
          }}
        >
          {display ? (
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {display}
            </Box>
          ) : (
            <Box
              component="span"
              sx={{
                color: COLORS.placeHolderColor,
              }}
            >
              {label}
            </Box>
          )}
          <InputAdornment position="end" sx={{ m: 0 }}>
            <CalendarTodayIcon fontSize="small" />
          </InputAdornment>
        </Box>
      </FormControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box p={1}>
          <DateCalendar
            value={localValue}
            onChange={handleDateSelect}
            views={["year", "month"]}
            openTo="month"
            sx={{
              "& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected":
                {
                  color: COLORS.iconColor,
                  backgroundColor: COLORS.GOLD,
                  "&:hover, &:focus": {
                    backgroundColor: COLORS.GOLD,
                  },
                },
            }}
          />
          <Box display="flex" justifyContent="space-between" px={2} pb={1}>
            <Button
              size="small"
              onClick={handleClear}
              sx={{
                color: COLORS.GOLD,
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{
                backgroundColor: COLORS.GOLD,
                color: COLORS.iconColor,
                "&:hover": {
                  backgroundColor: COLORS.GOLD,
                },
              }}
              onClick={() => setAnchorEl(null)}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default CustomMonthYearPicker;
